import { EStatusDoc } from '@configs/interface.config'

import { EItemCodeGroupBasis, EPolicyVersionStatus } from './item-code-group'

export enum EMonthlyDiscountConditionSeverity {
  INFO = 'INFO',
  DANGER = 'DANGER',
}

export type TGroupRateView = {
  itemCodeGroupId: number
  code: string
  displayName: string
  basis: EItemCodeGroupBasis
  unitLabel: string
  revenueUnitVnd: number | null
  ratePerUnit: number
  sortOrder: number
}

export type TMonthlyDiscountTier = {
  id: number
  label: string
  minAreaM2: number | null
  maxAreaM2: number | null
  minRevenue: number | null
  maxRevenue: number | null
  rates: TGroupRateView[]
  note: string | null
  sortOrder: number
}

export type TMonthlyDiscountEarlyPaymentRule = {
  id: number
  label: string
  dayFrom: number
  dayTo: number
  bonusPerM2: number
  highlight: boolean
  sortOrder: number
}

export type TMonthlyDiscountCondition = {
  id: number
  text: string
  severity: EMonthlyDiscountConditionSeverity
  sortOrder: number
}

export type TPolicyVersionSummary = {
  effectiveFrom: number
  status: EPolicyVersionStatus
  publishedAt: string | null
  publishedBy: number | null
  note: string | null
  /** = (status === 'DRAFT'), precomputed by BE so no client derives "can I edit this" differently. */
  editable: boolean
}

export type TPolicyDocumentGroup = {
  itemCodeGroupId: number
  code: string
  displayName: string
  basis: EItemCodeGroupBasis
  revenueUnitVnd: number | null
  unitLabel: string
  status: EStatusDoc
  memberCount: number
  sortOrder: number
}

export type TPolicyAccessoryRate = {
  itemCodeGroupId: number
  code: string
  displayName: string
  basis: EItemCodeGroupBasis
  revenueUnitVnd: number | null
  unitLabel: string
  status: EStatusDoc
  memberCount: number
  /** Phẳng — một mức cho cả phiên bản, không theo bậc. */
  ratePerUnit: number
  sortOrder: number
}

export type TPolicyAccessory = {
  minAreaM2: number | null
  minRevenue: number | null
  /** Dựng ở BE — client không tự ghép. Null khi chưa khai vế ngưỡng nào. */
  thresholdLabel: string | null
  rates: TPolicyAccessoryRate[]
}

export type TPolicyDocument = {
  version: TPolicyVersionSummary
  groups: TPolicyDocumentGroup[]
  /**
   * Nhóm được cộng vào ngưỡng sản lượng m² (ADR-038). Chọn tự do trong danh mục — **không nhất
   * thiết là tập con của `groups`**: nhóm chỉ có ở đây thì được đo, không được trả tiền.
   * ⚠️ **Rỗng = tính TẤT CẢ nhóm `AREA_M2` của lưới bậc**, không phải "không nhóm nào" — giữ nguyên
   * hành vi của mọi phiên bản ban hành trước ADR-038 mà không cần migration. Màn hình cấu hình
   * chọn sẵn toàn bộ trong trạng thái đó.
   */
  volumeGroupIds: number[]
  /** Metadata của đúng các nhóm trên — khỏi phải tra lại danh mục để lấy tên. */
  volumeGroups: TPolicyDocumentGroup[]
  tiers: TMonthlyDiscountTier[]
  accessory: TPolicyAccessory
  earlyPaymentRules: TMonthlyDiscountEarlyPaymentRule[]
  conditions: TMonthlyDiscountCondition[]
  availableVersions: TPolicyVersionSummary[]
}

// Số, không phải chuỗi — payload gửi lên khác kiểu với payload nhận về (xem PolicyDocument).
export type TWritePolicyDocument = {
  effectiveFrom: number
  note?: string | null
  tiers: Array<{
    label: string
    minAreaM2: number | null
    maxAreaM2: number | null
    minRevenue: number | null
    maxRevenue: number | null
    rates: Array<{ itemCodeGroupId: number; ratePerUnit: number }>
    note: string | null
    sortOrder: number
  }>
  // BẮT BUỘC như ba mảng còn lại: `PUT /config` mang ngữ nghĩa cả-tài-liệu, nên một khối bị quên
  // gửi sẽ âm thầm xoá sạch phần phụ kiện của chính sách (BE trả 400 nếu thiếu).
  accessory: {
    minAreaM2: number | null
    minRevenue: number | null
    rates: Array<{ itemCodeGroupId: number; ratePerUnit: number; sortOrder?: number }>
  }
  // BẮT BUỘC, cùng lý do với `accessory`: quên gửi sẽ âm thầm đưa chính sách về "tính tất cả".
  // Chỉ chi phối ngưỡng xét bậc — tiền chiết khấu và doanh số không đổi (ADR-038 §3).
  // Không cần có mặt trong `tiers[].rates[]`; phải là nhóm AREA_M2 và không thuộc khối phụ kiện.
  volumeGroupIds: number[]
  earlyPaymentRules: Array<{
    label: string
    dayFrom: number
    dayTo: number
    bonusPerM2: number
    highlight: boolean
    sortOrder: number
  }>
  conditions: Array<{
    text: string
    severity: EMonthlyDiscountConditionSeverity
    sortOrder: number
  }>
}

export type TCloneMonthlyDiscountPolicy = {
  fromEffectiveFrom: number
  toEffectiveFrom: number
  note?: string | null
}

export type TPolicyMissingRate = {
  tierId: number
  tierLabel: string
  itemCodeGroupId: number
  groupCode: string
}

// [ĐỔI v3.0, BE-102] Đơn vị đo hạ xuống bản ghi `item` — bắt được cả chồng lấn KHÁC trục
// (nhóm A memberType=ITEM_GROUP_CODE giữ 'PK', nhóm B memberType=ITEM_CODE giữ SKU 'PK-XYZ'
// thuộc chính PK — phép so chuỗi itemGroupCode cũ không bao giờ gom hai giá trị này lại).
export type TPolicyOverlapSample = {
  itemCode: string
  itemName: string
  groupIds: number[]
}

export type TPolicyGroupRef = {
  itemCodeGroupId: number
  groupCode: string
}

export type TPolicyEmptyGroup = TPolicyGroupRef

export type TPolicyValidationResult = {
  canPublish: boolean
  missingRates: TPolicyMissingRate[]
  overlappingCodes: { total: number; samples: TPolicyOverlapSample[] }
  emptyGroups: TPolicyEmptyGroup[]
  /** BR-MDISC-11c — nhóm nằm ở cả lưới bậc lẫn danh sách phụ kiện: sản lượng bị trả tiền hai lần. */
  groupsInBothBlocks: TPolicyGroupRef[]
  /** BR-MDISC-11d — có dòng phụ kiện nhưng cả hai vế ngưỡng đều null. Chặn ban hành, không chặn lưu nháp. */
  accessoryThresholdMissing: boolean
}

// ── Dealer read model, read by an admin on the dealer's behalf (RPT-010, yaml v2.5) ──────────
//
// These mirror `MonthlyDiscountResponse` on the server — the SAME shape the dealer's own app
// receives from `GET /v1/discount/monthly`. Keeping one shape is the point (BR-MDISC-18): a
// CMS-only variant would drift from the dealer's screen at the next formula change.

export type TGroupVolumeView = {
  itemCodeGroupId: number
  code: string
  displayName: string
  basis: EItemCodeGroupBasis
  quantity: number
  /** `1.234,5 m²` — built on the server; never assemble it here (BR-MDISC-09 owns the unit). */
  quantityLabel: string
  rate: number
  amount: number
  /**
   * Whether this row's volume is part of `totalAreaM2`, the figure the tier is chosen by.
   *
   * ⚠️ Since ADR-038 the m² rows do NOT sum to `totalAreaM2`, and they miss in BOTH directions:
   * a paid group may be excluded from the threshold (this flag explains that), and a group
   * counted towards the threshold may be paid nothing and so have no row here at all (this flag
   * cannot explain that one). Measured on real data 2026-09-15: three rows totalling 90.351,52 m²
   * all flagged false against a `totalAreaM2` of 77.189,13.
   */
  countsTowardAreaM2: boolean
}

export type TAccessoryItemView = {
  itemCodeGroupId: number
  code: string
  displayName: string
  basis: EItemCodeGroupBasis
  unitLabel: string
  ratePerUnit: number
  quantity: number
  quantityLabel: string
  /** Real money even while the block is locked — that is what lets the UI say what is being left on the table. */
  amount: number
  sortOrder: number
}

export type TAccessoryBlockView = {
  minAreaM2: number | null
  minRevenue: number | null
  thresholdLabel: string | null
  eligible: boolean
  /** Σ items[].amount when eligible, 0 when not. Never re-derive: the 0 is deliberate. */
  amount: number
  items: TAccessoryItemView[]
}

export type TMonthlyDiscountResponse = {
  hasData: boolean
  year: number
  month: number
  cycleLabel: string
  cycleStartAt: string
  cycleEndAt: string
  revenue: number
  /** m² **xét bậc** — tập nhóm khai ở "Nhóm tính sản lượng" (ADR-038). Không phải m² được trả tiền. */
  totalAreaM2: number
  /**
   * m² **tính chiết khấu** — Σ các dòng `AREA_M2` của `volumeByGroup`, tức đúng phần sản lượng
   * `estimatedDiscount` được làm ra từ đó (v2.6 / BE-106).
   *
   * Khác `totalAreaM2` là chuyện bình thường, không phải lỗi: đo thật 2026-09-15 cho ra
   * 90.351,52 ở đây so với 77.189,13 ở kia. Màn hình phải hiện **cả hai**, có nhãn.
   */
  discountAreaM2: number
  volumeByGroup: TGroupVolumeView[]
  unclassifiedAreaM2: number
  /** False ⇒ render `estimatedDiscount` as "chưa tính được", never as `0 đ` (BR-MDISC-05). */
  areaDataAvailable: boolean
  currentTier: TMonthlyDiscountTier | null
  nextTier: TMonthlyDiscountTier | null
  revenueNeededForNext: number | null
  areaNeededForNext: number | null
  estimatedDiscount: number
  accessory: TAccessoryBlockView
  tierLadder: TMonthlyDiscountTier[]
  earlyPaymentRules: TMonthlyDiscountEarlyPaymentRule[]
  conditions: TMonthlyDiscountCondition[]
  closingDate: string
}

export type TDealerIdentity = {
  id: number
  code: string | null
  name: string | null
  phone: string | null
  isActive: number
}

export type TAdminDealerMonthlyDiscount = {
  dealer: TDealerIdentity
  /**
   * ⚠️ Read this BEFORE rendering any figure. False means the user has no Bravo customer code, so
   * there were no order rows to read at all — a response identical to a dealer who bought plenty
   * and reached no tier. The numbers cannot tell the two apart; only this flag can (BR-MDISC-19).
   */
  bravoLinked: boolean
  discount: TMonthlyDiscountResponse
}
